/** Client-side used-word history + unused backlog for Hangman. */

const USED_KEY = "hm-used-words-v1";
const BACKLOG_KEY = "hm-word-backlog-v1";
export const MAX_USED_WORDS = 50;
/** How many extras to request beyond the current match size. */
export const BACKLOG_FETCH_COUNT = 24;

function normalizeWord(raw) {
  return String(raw || "")
    .toUpperCase()
    .replace(/['’`-]/g, "")
    .replace(/[^A-Z\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore quota / private mode
  }
}

export function backlogStorageKey(category, difficulty, customCategory = "") {
  const custom = String(customCategory || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .slice(0, 40);
  return `${String(category || "").toLowerCase()}|${String(difficulty || "").toLowerCase()}|${custom}`;
}

export function getRecentUsedWords() {
  const list = readJson(USED_KEY, []);
  if (!Array.isArray(list)) return [];
  const out = [];
  const seen = new Set();
  for (const item of list) {
    const w = normalizeWord(item);
    if (!w || seen.has(w)) continue;
    seen.add(w);
    out.push(w);
  }
  return out.slice(-MAX_USED_WORDS);
}

/** Append played words; keep only the most recent MAX_USED_WORDS. */
export function recordUsedWords(words) {
  const prev = getRecentUsedWords();
  const seen = new Set(prev);
  const next = [...prev];
  for (const item of words || []) {
    const w = normalizeWord(item);
    if (!w) continue;
    if (seen.has(w)) {
      // move to end so it stays in the recent window
      const idx = next.indexOf(w);
      if (idx >= 0) next.splice(idx, 1);
    } else {
      seen.add(w);
    }
    next.push(w);
  }
  writeJson(USED_KEY, next.slice(-MAX_USED_WORDS));
  return getRecentUsedWords();
}

export function getBacklog(storageKey) {
  const all = readJson(BACKLOG_KEY, {});
  if (!all || typeof all !== "object") return [];
  const list = all[storageKey];
  if (!Array.isArray(list)) return [];
  const out = [];
  const seen = new Set();
  for (const item of list) {
    const w = normalizeWord(item);
    if (!w || seen.has(w)) continue;
    seen.add(w);
    out.push(w);
  }
  return out;
}

export function setBacklog(storageKey, words) {
  const all = readJson(BACKLOG_KEY, {});
  const next = all && typeof all === "object" ? { ...all } : {};
  const cleaned = [];
  const seen = new Set();
  for (const item of words || []) {
    const w = normalizeWord(item);
    if (!w || seen.has(w)) continue;
    seen.add(w);
    cleaned.push(w);
  }
  if (cleaned.length) next[storageKey] = cleaned;
  else delete next[storageKey];
  writeJson(BACKLOG_KEY, next);
  return cleaned;
}

export function shuffleWords(words) {
  const arr = [...(words || [])];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = arr[i];
    arr[i] = arr[j];
    arr[j] = tmp;
  }
  return arr;
}

/**
 * Prefer unused backlog words; refill from `fetchMore` when short.
 * @param {{
 *   need: number,
 *   storageKey: string,
 *   fetchMore: (args: { count: number, exclude: string[] }) => Promise<string[]>
 * }} opts
 */
export async function takeWordsFromPool({ need, storageKey, fetchMore }) {
  const used = new Set(getRecentUsedWords());
  let backlog = getBacklog(storageKey).filter((w) => !used.has(w));

  async function refill() {
    const exclude = [...new Set([...used, ...backlog])];
    const fetchCount = Math.max(BACKLOG_FETCH_COUNT, need * 5);
    const fresh = await fetchMore({ count: fetchCount, exclude });
    const seen = new Set(backlog);
    for (const item of fresh || []) {
      const w = normalizeWord(item);
      if (!w || used.has(w) || seen.has(w)) continue;
      seen.add(w);
      backlog.push(w);
    }
  }

  if (backlog.length < need) await refill();
  if (backlog.length < need) await refill();

  const shuffled = shuffleWords(backlog);
  const picked = shuffled.slice(0, need);
  if (picked.length < need) {
    throw new Error(
      `Only found ${picked.length} of ${need} unused words. Tap Start game to try again.`
    );
  }

  setBacklog(storageKey, shuffled.slice(need));
  recordUsedWords(picked);
  return picked;
}
